/* @flow */
import { Set, Map, List, fromJS, OrderedMap } from 'immutable';
import { combineReducers } from 'redux-immutable';
import type { Action } from 'trustar/utils/redux-types.js';
import { encodeId } from 'trustar/utils';

import { IndicatorTag } from 'modules/tags/types';
import {
  PAGINATE_INDICATOR_LIST,
  REFRESH_INDICATOR_LIST,
  SET_TIME_RANGE_SUCCESS,
  FETCH_INDICATOR_LIBRARY,
  FETCH_INDICATOR_LIBRARY_SUCCESS,
  FETCH_INDICATOR_LIBRARY_FAILURE,
  FETCH_IOC_ENRICHMENT,
  FETCH_IOC_ENRICHMENT_SUCCESS,
  FETCH_IOC_ENRICHMENT_FAILURE,
  SET_INDICATOR_FETCH_OPTIONS,
  CLEAR_INDICATORS,
  FETCH_INDICATOR_METADATA,
  FETCH_INDICATOR_METADATA_SUCCESS,
  REVERT_CLASSIFICATION,
  REVERT_CLASSIFICATION_SUCCESS,
  REVERT_CLASSIFICATION_FAILURE,
  EXPORT_INDICATORS_LIST,
  EXPORT_INDICATORS_LIST_SUCCESS,
  EXPORT_INDICATORS_LIST_FAILURE,
  EXPORT_INDICATORS_FOR_TAG,
  EXPORT_INDICATORS_FOR_TAG_SUCCESS,
  EXPORT_INDICATORS_FOR_TAG_FAILURE,
  FETCH_INDICATORS_FOR_TAG,
  FETCH_INDICATORS_FOR_TAG_SUCCESS,
  FETCH_INDICATORS_FOR_TAG_FAILURE,
} from './action-types.js';
import {
  REMOVE_INDICATOR_TAG_SUCCESS,
} from 'modules/tags/action-types';
import { SAVE_NOTE_SUCCESS, DELETE_NOTE_SUCCESS } from '../constellation/action-types';
import type {
  IndicatorLibrary,
  IndicatorEnrichment,
  IndicatorMetadata,
  TagCollection,
} from './types';
import {
  defaultRange,
  DefaultLibrary,
  DefaultEnrichment,
  DefaultIndicatorMetadata,
  DefaultTagCollection,
} from './types';
import { normalizeIndicatorShape, indicatorToId } from './utils';
import {STORE_INDICATOR_LOCAL} from './action-types';

/* Map the metadata to Immutable. Mainly complicated because we're using these Records to type
 * the various objects, like IndicatorTag and the Metadata Object itself. */
const saveMetadataFromJson = (metadata: Array<Object>): Map<string, IndicatorMetadata> => {
  return fromJS(metadata)
    .reduce((newState, rawMetadata) => {
      const metadata = DefaultIndicatorMetadata(rawMetadata)
        .set('loading', false)
        .update('enclaves', enclaves => Set(enclaves))
        .update('tags', tags => tags.map(IndicatorTag));
      return newState.set(rawMetadata.get('id'), metadata);
    }, Map());
};

/* Map request for metadata to store in state, mainly to establish loading state. */
const saveMetadataFromRequest = (data: Array<Object>): Map<string, IndicatorMetadata> => {
  return data.reduce((newState, ioc) => {
    return newState.set(ioc.id, DefaultIndicatorMetadata(ioc).set('loading', true));
  }, Map());
};

/* A cache of indicator metadata. Operates similar to report metadata. */
export const metadata = (state: Map<string, IndicatorMetadata> = Map(), action: Action<*>) => {
  const { type, payload } = action;

  switch (type) {
  case FETCH_INDICATOR_LIBRARY_SUCCESS:
    return state.merge(saveMetadataFromRequest(payload.items || []));
  case FETCH_INDICATOR_METADATA_SUCCESS:
    return state.merge(saveMetadataFromJson(payload));
  case REMOVE_INDICATOR_TAG_SUCCESS:
    return state.updateIn([payload.id, 'tags'], tags => tags.filter(tag => tag.get('guid') !== payload.guid));
  case SAVE_NOTE_SUCCESS:
    return state.updateIn([payload.nodeGuid, 'noteCount'], count => count + 1);
  case DELETE_NOTE_SUCCESS:
    return state.updateIn([payload.nodeGuid, 'noteCount'], count => count - 1);
  default:
    return state;
  }
};

/* Store for the IOC library */
export const library = (state: IndicatorLibrary = DefaultLibrary(), action: Action<*>) => {
  const { type, payload } = action;

  switch (type) {
  case CLEAR_INDICATORS:
    return state.set('indicatorList', OrderedMap());
  case REFRESH_INDICATOR_LIST:
    return state
      .set('currentDisplayRange', defaultRange)
      .set('loading', true)
      .set('indicatorList', OrderedMap());
  case STORE_INDICATOR_LOCAL:
    const indicatorId = payload.has('id') ? payload.get('id') : payload.get('guid');
    return state
    // $FlowIssue: update is a method of Record, Immutable 4 will fix this.
      .update('indicatorList', indicatorList => indicatorList.set(indicatorId, payload));
  case FETCH_INDICATOR_LIBRARY:
    return state
      .set('loading', true)
      .set('indicatorList', OrderedMap());
  case PAGINATE_INDICATOR_LIST:
    return state
      .set('loading', true);
  case SET_TIME_RANGE_SUCCESS:
    return state.set('currentDisplayRange', payload);
  case SET_INDICATOR_FETCH_OPTIONS:
    // $FlowIssue: update is a method of Record, Immutable 4 will fix this.
    return state.update('fetchParams', params => params.merge(fromJS(payload)));
  case FETCH_INDICATOR_LIBRARY_SUCCESS:
    return state.set('loading', false).set('error', false)
      // $FlowIssue: update is a method of Record, Immutable 4 will fix this.
      .update('fetchParams', options => options.set('hasNext', payload.hasNext).set('page', payload.page))
      .update('indicatorList', list => fromJS(payload.items)
        .reduce((newList, item) => newList.set(item.get('id'), item), list));
  case FETCH_INDICATOR_LIBRARY_FAILURE:
    return state.set('loading', false).set('error', true);
  default:
    return state;
  }
};

/* The full map of IOC enrichment */
export const enrichment = (state: IndicatorEnrichment = DefaultEnrichment(), action: Action<*>) => {
  const { type, payload } = action;

  switch (type) {
  case FETCH_IOC_ENRICHMENT:
  case REVERT_CLASSIFICATION:
    return state.set('loading', true);
  case REVERT_CLASSIFICATION_SUCCESS:
    return state.set('loading', false);
  case FETCH_IOC_ENRICHMENT_SUCCESS:
    return state
      .set('loading', false)
      .setIn(['data', payload.id], payload.enrichment);
  case FETCH_IOC_ENRICHMENT_FAILURE:
  case REVERT_CLASSIFICATION_FAILURE:
    return state
      .set('error', true)
      .set('loading', false);
  default:
    return state;
  }
};

/* Store to hold most recent indicators for a given tag. Refreshed whenever a new tag node is selected
 * in the constellation. Therefore, indicators in this store always correspond to the currently selected
 * tag. */
export const tagCollection = (state: TagCollection = DefaultTagCollection(), action: Action<*>) => {
  const { type, payload } = action;

  switch (type) {
  case FETCH_INDICATORS_FOR_TAG:
    return state
      .set('loading', true)
      .set('indicators', List());
  case FETCH_INDICATORS_FOR_TAG_SUCCESS:
    return state
      .set('loading', false)
      .set('indicators', payload);
  case FETCH_INDICATORS_FOR_TAG_FAILURE:
    return state
      .set('loading', false)
      .set('indicators', List());
  default:
    return state;
  }
};

export const exportLoading = (state: boolean = false, action: Action<*>) => {
  const { type } = action;

  switch (type) {
  case EXPORT_INDICATORS_LIST:
  case EXPORT_INDICATORS_FOR_TAG:
    return true;
  case EXPORT_INDICATORS_LIST_SUCCESS:
  case EXPORT_INDICATORS_LIST_FAILURE:
  case EXPORT_INDICATORS_FOR_TAG_SUCCESS:
  case EXPORT_INDICATORS_FOR_TAG_FAILURE:
    return false;
  default:
    return state;
  }
};

export default combineReducers({
  metadata,
  library,
  enrichment,
  tagCollection,
  exportLoading,
});



// WEBPACK FOOTER //
// ./src/app/modules/indicators/stores.js